import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
} from "@/components/ui";
import { Download, RefreshCw, TrendingUp, TrendingDown } from "lucide-react";
import reportsService from "@/services/reportsService";
import { SubsidiaryFilter, DateRangeFilter } from "../filters";

/**
 * Profit & Loss Statement Component
 *
 * Displays revenue, expenses, and profitability with period comparison.
 *
 * Requirements: 6.1-6.10
 */
const ProfitLossStatement = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [statement, setStatement] = useState(null);
  const [filters, setFilters] = useState({
    subsidiary_id: "",
    date_from: "",
    date_to: "",
    comparison_period: "",
  });

  useEffect(() => {
    if (filters.date_from && filters.date_to) {
      loadStatement();
    }
  }, []);

  const loadStatement = async () => {
    if (!filters.date_from || !filters.date_to) {
      return;
    }

    try {
      setLoading(true);
      const response = await reportsService.getProfitLoss(filters);

      if (response?.data?.success) {
        setStatement(response.data.data);
      }
    } catch (error) {
      console.error("Failed to load P&L statement:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    loadStatement();
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const exportData = {
        reportData: statement,
        reportType: "profit_loss",
        reportTitle: `Profit & Loss Statement (${filters.date_from} to ${filters.date_to})`,
        format,
        filters,
        auditTrail: "summary",
      };

      const response =
        format === "pdf"
          ? await reportsService.exportToPDF(exportData)
          : await reportsService.exportToExcel(exportData);

      if (response?.data?.success) {
        const downloadResponse = await reportsService.downloadExport(
          response.data.data.history_id,
        );
        const url = window.URL.createObjectURL(
          new Blob([downloadResponse.data]),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", response.data.data.filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount || 0).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatPercentage = (value) => {
    return `${parseFloat(value || 0).toFixed(1)}%`;
  };

  const renderVariance = (variance) => {
    if (!variance) return null;
    const isPositive = variance.amount >= 0;
    return (
      <div className="flex items-center gap-2">
        {isPositive ? (
          <TrendingUp className="h-4 w-4 text-green-600" />
        ) : (
          <TrendingDown className="h-4 w-4 text-red-600" />
        )}
        <span className={isPositive ? "text-green-600" : "text-red-600"}>
          {formatCurrency(Math.abs(variance.amount))} (
          {formatPercentage(variance.percentage)})
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Profit & Loss Statement
          </h1>
          <p className="text-gray-600 mt-1">
            Income statement showing revenue, expenses, and profitability
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleExport("pdf")}
            disabled={exporting || !statement}
          >
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport("excel")}
            disabled={exporting || !statement}
          >
            <Download className="h-4 w-4 mr-2" />
            Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SubsidiaryFilter
              value={filters.subsidiary_id}
              onChange={(value) =>
                setFilters({ ...filters, subsidiary_id: value })
              }
            />
            <DateRangeFilter
              dateFrom={filters.date_from}
              dateTo={filters.date_to}
              onDateFromChange={(value) =>
                setFilters({ ...filters, date_from: value })
              }
              onDateToChange={(value) =>
                setFilters({ ...filters, date_to: value })
              }
            />
            <div className="space-y-2">
              <Label>Comparison Period</Label>
              <Select
                value={filters.comparison_period}
                onValueChange={(value) =>
                  setFilters({ ...filters, comparison_period: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="No comparison" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No comparison</SelectItem>
                  <SelectItem value="previous_period">
                    Previous Period
                  </SelectItem>
                  <SelectItem value="previous_year">Previous Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={handleApplyFilters} disabled={loading}>
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                "Generate Statement"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Statement */}
      {loading ? (
        <Card>
          <CardContent className="p-8 text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Generating statement...</p>
          </CardContent>
        </Card>
      ) : statement ? (
        <Card>
          <CardHeader>
            <CardTitle>
              Profit & Loss Statement
              {statement.period && (
                <span className="text-sm font-normal text-gray-600 ml-2">
                  {statement.period.date_from} to {statement.period.date_to}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-1/2">Account</TableHead>
                    <TableHead className="text-right">Current Period</TableHead>
                    {statement.comparison && (
                      <>
                        <TableHead className="text-right">
                          {statement.comparison.period_label}
                        </TableHead>
                        <TableHead className="text-right">Variance</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Revenue Section */}
                  <TableRow className="bg-blue-50 font-semibold">
                    <TableCell colSpan={statement.comparison ? 4 : 2}>
                      REVENUE
                    </TableCell>
                  </TableRow>
                  {statement.revenue?.accounts?.map((account, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="pl-8">{account.name}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(account.amount)}
                      </TableCell>
                      {statement.comparison && (
                        <>
                          <TableCell className="text-right">
                            {formatCurrency(account.comparison_amount)}
                          </TableCell>
                          <TableCell className="text-right">
                            {renderVariance(account.variance)}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  <TableRow className="font-semibold border-t">
                    <TableCell>Total Revenue</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(statement.revenue?.total)}
                    </TableCell>
                    {statement.comparison && (
                      <>
                        <TableCell className="text-right">
                          {formatCurrency(statement.revenue?.comparison_total)}
                        </TableCell>
                        <TableCell className="text-right">
                          {renderVariance(statement.revenue?.variance)}
                        </TableCell>
                      </>
                    )}
                  </TableRow>

                  {/* Expenses Section */}
                  <TableRow className="bg-red-50 font-semibold">
                    <TableCell colSpan={statement.comparison ? 4 : 2}>
                      EXPENSES
                    </TableCell>
                  </TableRow>
                  {statement.expenses?.accounts?.map((account, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="pl-8">{account.name}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(account.amount)}
                      </TableCell>
                      {statement.comparison && (
                        <>
                          <TableCell className="text-right">
                            {formatCurrency(account.comparison_amount)}
                          </TableCell>
                          <TableCell className="text-right">
                            {renderVariance(account.variance)}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  <TableRow className="font-semibold border-t">
                    <TableCell>Total Expenses</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(statement.expenses?.total)}
                    </TableCell>
                    {statement.comparison && (
                      <>
                        <TableCell className="text-right">
                          {formatCurrency(statement.expenses?.comparison_total)}
                        </TableCell>
                        <TableCell className="text-right">
                          {renderVariance(statement.expenses?.variance)}
                        </TableCell>
                      </>
                    )}
                  </TableRow>

                  {/* Net Income */}
                  <TableRow className="bg-gray-100 font-bold text-lg border-t-2">
                    <TableCell>NET INCOME</TableCell>
                    <TableCell
                      className={`text-right ${
                        statement.net_income?.amount >= 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {formatCurrency(statement.net_income?.amount)}
                    </TableCell>
                    {statement.comparison && (
                      <>
                        <TableCell className="text-right">
                          {formatCurrency(
                            statement.net_income?.comparison_amount,
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {renderVariance(statement.net_income?.variance)}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Key Metrics */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Gross Profit Margin</p>
                <p className="text-2xl font-bold">
                  {formatPercentage(statement.metrics?.gross_profit_margin)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Operating Margin</p>
                <p className="text-2xl font-bold">
                  {formatPercentage(statement.metrics?.operating_margin)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Net Profit Margin</p>
                <p className="text-2xl font-bold">
                  {formatPercentage(statement.metrics?.net_profit_margin)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            Select a date range and click "Generate Statement" to view the
            report.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProfitLossStatement;
